import React, { useEffect, useRef, useState } from 'react';

interface CursorEffectProps {
  // Optional class for customizing the cursor effect
  className?: string;
}

export function CursorEffect({ className = '' }: CursorEffectProps) {
  const [mousePosition, setMousePosition] = useState({ x: -1000, y: -1000 });
  const [isVisible, setIsVisible] = useState(false);
  const cursorRef = useRef<HTMLDivElement>(null);
  const animationFrameId = useRef<number | null>(null);
  const lastMousePosition = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    // Function to update CSS variables for inner light effects
    const updateLightingEffects = (x: number, y: number) => {
      const elements = document.querySelectorAll('.inner-light');
      elements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        // Calculate distances from mouse to element center (normalized to [0, 100])
        const distanceX = Math.max(0, Math.min(100, ((x - centerX) / rect.width) * 200 + 50));
        const distanceY = Math.max(0, Math.min(100, ((y - centerY) / rect.height) * 200 + 50));

        // Update CSS variables for the radial gradient position
        element.setAttribute('style', `--x: ${distanceX}%; --y: ${distanceY}%;`);
      });
    };

    // Smoother animation for cursor movement
    const animateCursor = () => {
      if (!cursorRef.current) return;

      // Smooth follow with easing
      const ease = 0.13; // Adjust for faster/slower following

      // Calculate the distance to move
      const dx = mousePosition.x - lastMousePosition.current.x;
      const dy = mousePosition.y - lastMousePosition.current.y;

      lastMousePosition.current = {
        x: lastMousePosition.current.x + dx * ease,
        y: lastMousePosition.current.y + dy * ease,
      };

      // Apply the new position
      cursorRef.current.style.transform = `translate(${lastMousePosition.current.x}px, ${lastMousePosition.current.y}px)`;

      // Continue the animation loop
      animationFrameId.current = requestAnimationFrame(animateCursor);
    };

    // Start the animation on component mount
    animationFrameId.current = requestAnimationFrame(animateCursor);

    // Track mouse position and update on mouse move
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      updateLightingEffects(e.clientX, e.clientY);

      // Show the cursor effect if it's hidden
      if (!isVisible) {
        setIsVisible(true);
      }
    };

    // Hide the cursor if mouse leaves the window
    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    // Enter with a slight delay to avoid initial flash on page load
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    window.addEventListener('mousemove', handleMouseMove);
    document.body.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timeout);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [isVisible]);

  return <div ref={cursorRef} className={className} aria-hidden="true" />;
}

export default CursorEffect;
